import React, { useEffect, useState } from 'react';
import { ConfirmV2, isMobileScreen } from '@apply/ui';
import {
    Expander,
    ExpanderTheme,
    ExpanderButtonStyle,
    ExpanderBorderStyle,
    Panel,
    PanelTheme,
    PanelBorderStyle,
    PanelBorderRadius,
    IconButton,
    IconButtonTheme,
    IconButtonSize,
    renderCustomCountExpanderHeader,
} from '@apply/components';
import { ProgressDonut, ProgressColor } from '@apply/components/src/components/controls/ProgressDonut';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { IDashboardApplicationItem } from '@apply/api/src/api/ApplyApi';
import '../scss/_homepanels.scss';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateApiId } from '@apply/store/src/borrower/actions';

interface ApplyDraftPanelParameters {
    applications: IDashboardApplicationItem[];
    deleteApplication: (applicationId: number) => void;
}

export const ApplyDraftPanel: React.FC<ApplyDraftPanelParameters> = ({ applications, deleteApplication }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const mobileScreen = isMobileScreen();

    const [openDeleteConfirmPopup, set_openDeleteConfirmPopup] = useState<boolean>(false);
    const [applicationIdToDelete, set_applicationIdToDelete] = useState<number | undefined>(undefined);
    const [redirectUrl, set_redirectUrl] = useState<string | undefined>(undefined);

    useEffect(() => {
        set_openDeleteConfirmPopup(!!applicationIdToDelete);
    }, [applicationIdToDelete]);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl);
        }
    }, [redirectUrl]);

    const onConfirmDelete = () => {
        if (!applicationIdToDelete) return;

        deleteApplication(applicationIdToDelete);
        set_applicationIdToDelete(undefined);
    };

    const onCancelDelete = () => {
        set_applicationIdToDelete(undefined);
        set_openDeleteConfirmPopup(false);
    };

    const onAddressClick = (apiId: string) => {
        dispatch(updateApiId(apiId));
        set_redirectUrl(`/borrower/${apiId}`);
    };

    const AddressBlock = ({ apiId, addresses }: { apiId: string; addresses: string[] }) => {
        if (addresses.length < 1) return <a onClick={() => onAddressClick(apiId)}>(no address)</a>;

        return addresses.map((addr, i) => (
            <div
                key={i}
                className='addressItem'
            >
                <a onClick={() => onAddressClick(apiId)}>{addr ? addr : '(no address)'}</a>
            </div>
        ));
    };

    return (
        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            <Expander
                header={renderCustomCountExpanderHeader(applications.length, `${!mobileScreen ? 'Apply Draft' : 'Draft'}`)}
                theme={ExpanderTheme.White}
                buttonStyle={ExpanderButtonStyle.Rounded}
                borderStyle={ExpanderBorderStyle.Rounded}
                initialExpanded={true}
            >
                {applications.map((app) => {
                    const addresses = app.properties.map((prop) => {
                        const { street1, street2, city } = prop;
                        return [street1, street2, city].filter(Boolean).join(', ');
                    });
                    const percentComplete = app.percentComplete;
                    const progressColor =
                        percentComplete < 25
                            ? ProgressColor.Red
                            : percentComplete < 50
                              ? ProgressColor.Orange
                              : percentComplete < 75
                                ? ProgressColor.Blue
                                : ProgressColor.Green;

                    return (
                        <div
                            key={app.applicationId}
                            style={{ marginLeft: '20px', marginRight: '20px' }}
                        >
                            <Panel
                                key={app.properties.map((prop) => prop.id).join('-')}
                                padding={10}
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Small}
                                borderStyle={PanelBorderStyle.Solid}
                            >
                                <div className='panelContent applyDraft'>
                                    <div className='addressList'>
                                        <AddressBlock
                                            apiId={app.appId}
                                            addresses={addresses}
                                        />
                                    </div>
                                    <div className='panelActions'>
                                        <span className='donutWrapper'>
                                            <ProgressDonut
                                                value={percentComplete}
                                                size={45}
                                                progressColor={progressColor}
                                            />
                                        </span>
                                        <IconButton
                                            id='Delete Item'
                                            theme={IconButtonTheme.Red}
                                            handleClick={() => set_applicationIdToDelete(app.applicationId)}
                                            size={IconButtonSize.Small}
                                            icon={faTrashCan}
                                            hint='Delete Item'
                                        />
                                    </div>
                                </div>
                            </Panel>
                        </div>
                    );
                })}
            </Expander>
            <ConfirmV2
                id='deletePendingApplicationConfimModal'
                title='Delete Application'
                okLabel='Yes'
                cancelLabel='No'
                open={openDeleteConfirmPopup}
                onConfirm={onConfirmDelete}
                onCancel={onCancelDelete}
                height='200px'
                width='400px'
            >
                Are you sure you want to delete this application?
            </ConfirmV2>
        </div>
    );
};
